import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { useEscapeLayer, useFocusTrap } from '../components/ui/layers';
import type { ZoomTarget } from './HelpArticle';
import styles from './Help.module.css';

/** A screenshot full size: fit to the screen or actual size (scroll / pinch-zoom), Escape closes. */
export function ImageZoom({ target, onClose }: { target: ZoomTarget | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [actual, setActual] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEscapeLayer(!!target, onClose);
  useFocusTrap(box, !!target);
  if (!target) return null;
  return createPortal(
    <div
      ref={box}
      className={styles.zoom}
      role="dialog"
      aria-modal="true"
      aria-label={target.alt || t('help.zoom.title')}
      tabIndex={-1}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.zoomBar}>
        <Button variant="secondary" size="sm" onClick={() => setActual((a) => !a)} aria-pressed={actual}>
          {actual ? t('help.zoom.fit') : t('help.zoom.actual')}
        </Button>
        <Button variant="primary" size="sm" onClick={onClose} data-autofocus="">
          {t('help.zoom.close')}
        </Button>
      </div>
      <div className={`${styles.zoomStage} ${actual ? styles.zoomActual : ''}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <img src={target.src} alt={target.alt} />
      </div>
    </div>,
    document.body,
  );
}
